// Client-side mirror of the backend alias-key normalization
// (functions/python/chariot/library/__init__.py: `slugify` /
// `normalize_query_key`). The cache-hit fast path reads
// drug_aliases/{normalizeQueryKey(query)} directly, so these MUST produce the
// exact keys the Python originals write. A divergence is only ever safe in the
// miss direction (the backend path takes over) — which is why the separator
// set below is copied verbatim (symbols AND word connectors) instead of
// reusing suggestion-rows' symbol-only splitter.

const SLUG_RE = /[^a-z0-9]+/g;

// Combination separators — verbatim from the Python `_SEPARATOR`. A query
// carrying one of these is a multi-substance set / joined string.
const SEPARATOR_RE = /\s*(?:\+|\/|&|,|\band\b|\bi\b|\bz\b|\bwith\b)\s*/i;

// Slug a name/query into a stable Firestore document id: lower-case, collapse
// every run of non-alphanumerics to a single "-", trim dashes. Empty or
// symbol-only input collapses to "unknown" (a valid, non-empty id).
export function slugify(text: string | null | undefined): string {
  const slug = (text ?? "")
    .trim()
    .toLowerCase()
    .replace(SLUG_RE, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "unknown";
}

// Normalize a raw user query into an alias-map key. A single-substance query
// keeps the plain slug; a combination (any separator present) normalizes to
// the sorted, lowercased token set first, so token order and the separator
// used don't fork the cache key. Sorting compares code units (Python's
// `sorted()` semantics) — NOT locale order — to stay byte-identical.
export function normalizeQueryKey(query: string | null | undefined): string {
  const tokens = (query ?? "")
    .split(SEPARATOR_RE)
    .map((token) => token.trim())
    .filter(Boolean);
  if (tokens.length > 1) {
    const sorted = tokens
      .map((token) => token.toLowerCase())
      .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
    return slugify(sorted.join("+"));
  }
  return slugify(query);
}
